/**
 * One person's contribution to one dataset: edits, cells completed and help
 * requests. Shared by the profile's Datasets tab and the "Now entering"
 * card, so the two can never show different numbers.
 *
 * ONE SOURCE, AND ONLY THE GAME (Ames 2026-10-07: "stats for this game
 * should only include things that were done in this game"). Cells completed
 * are the game's own log by the completed cell rule (completion_rule.ts,
 * the same rule as the SQL view ew_cell_completions that the leaderboard and
 * the career counter users.cells_completed are built on). They used to be
 * joined with CAVE's copy of each dataset's own records, which also holds
 * cells a player marked in other tools: a profile said 360 cells completed
 * while its Retina card said 365, and a BANC card said 1,939 for work never
 * done here. Summed over datasets, the cards now equal the career number.
 */
import { segLayerName, canonicalDataset, currentDatasetTag, findDatasetByCanonical, type DatasetEntry } from '../datasets';
import { completedCells, datasetKey, type CompletedCell, type CompletionLogRow } from './completion_rule';
import { caveMirrorCells, editHours, helpRequestDatasets } from './player_summary';

export interface DatasetContribution {
  edits: number; completions: number; helpRequests: number;
  /** Cells the dataset's own records credit to this player that were not
   *  completed in the game (BANC, FlyWire...). Shown on the dataset card as
   *  its own number and counted nowhere else: not in cells completed, the
   *  career number, the leaderboard or any Achievement (Ames 2026-10-07:
   *  "they should show in dataset stats but not count toward achievements"). */
  outside: number;
}

/**
 * Datasets whose work happens in the game, so edits and help requests there
 * are the player's whole record. Everywhere else (BANC, FlyWire, MICrONS...)
 * most of a player's work was done in other tools, and "0 edits" beside
 * 1,939 cells reads as wrong (Ames 2026-10-06): those cards lead with cells
 * proofread and show edits or help requests only when there are some.
 */
const GAME_NATIVE = new Set(['stroeh_mouse_retina', 'pni_mec', 'pinky_sandbox']);
export function showsAllStats(ds: DatasetEntry): boolean {
  return GAME_NATIVE.has(ds.id);
}

/** Every name a dataset has been logged under. */
export function datasetTagVariants(ds: DatasetEntry): string[] {
  const canon = canonicalDataset(segLayerName(ds));
  // edit_log.dataset DEFAULTs to 'eyewire_ii', so any row written without an
  // explicit tag carries that legacy retina name.
  const legacy = canon === 'stroeh_mouse_retina' ? ['eyewire_ii', 'eyewire_ii_retina'] : [];
  return [...new Set([canon, ds.id, segLayerName(ds), ...legacy])];
}

/**
 * The cells a player has completed on a dataset, from the app's own log
 * (edit_log) by the leaderboard's rule (completion_rule.ts, the same rule as
 * the SQL view ew_cell_completions). The CAVE mirror only fills from CAVE's
 * materialized snapshots, which never include the EyeWire table on MICrONS
 * and do not exist for MEC, so those datasets always showed 0 cells
 * (Ames 2026-10-04). Null when the log cannot be read.
 *
 * The rule needs the player's whole completion history on every dataset
 * (when a cell was completed is decided before anything is filtered), so
 * this reads all of it, a page at a time, and keeps it for a minute. The
 * read itself is kept, not only its result: the Datasets tab asks for all 15
 * datasets at once, and each used to start its own full read (2026-10-25).
 */
let logCache: { uid: string; at: number; cells: Promise<CompletedCell[] | null> } | null = null;
async function readLoggedCells(uid: string): Promise<CompletedCell[] | null> {
  const { supabase } = await import('../supabase');
  const rows: CompletionLogRow[] = [];
  for (let from = 0; ; from += 1000) {
    // The log's time column is `timestamp`. (It was read as `created_at`,
    // which does not exist, so this always failed and the mirror's number
    // was shown alone: audit of 2026-10-05.)
    const { data, error } = await supabase.from('edit_log')
      .select('id,operation,metadata,task_id,dataset,success,timestamp')
      .eq('user_id', uid)
      .in('operation', ['complete_task', 'mark_complete', 'unmark_complete'])
      .order('timestamp', { ascending: true }).order('id', { ascending: true })
      .range(from, from + 999);
    if (error || !data) return null;
    rows.push(...(data as any[]));
    if (data.length < 1000) break;
  }
  return completedCells(rows);
}
async function loggedCells(uid: string, tags: string[]): Promise<CompletedCell[] | null> {
  try {
    if (!logCache || logCache.uid !== uid || Date.now() - logCache.at > 60_000) {
      const entry = { uid, at: Date.now(), cells: readLoggedCells(uid).catch(() => null) };
      logCache = entry;
      void entry.cells.then(c => { if (c === null && logCache === entry) logCache = null; });
    }
    const cells = await logCache.cells;
    if (!cells) return null;
    const keys = new Set(tags.map(datasetKey));
    return cells.filter(c => keys.has(c.dataset));
  } catch { return null; }
}

/**
 * Both numbers of the completion celebration from ONE place: the database's
 * own list of a player's completed cells (ew_cell_completions, the rule the
 * leaderboard uses). They used to come from two: the dataset's number from
 * the CAVE mirror joined with the log, the overall number from a counter on
 * the profile. The two disagreed, and the celebration said "your EyeWire II
 * total is 72" over "63 cells across all datasets" (Ames 2026-10-07). Counted
 * from one list, a dataset can never have more cells than all datasets do.
 * Null when the list can not be read.
 */
export async function celebrationCellCounts(uid: string): Promise<{ label: string; here: number; all: number } | null> {
  const ds = findDatasetByCanonical(canonicalDataset(currentDatasetTag()));
  try {
    const { supabase } = await import('../supabase');
    const rows: { dataset: string }[] = [];
    for (let from = 0; ; from += 1000) {
      const { data, error } = await supabase.from('ew_cell_completions').select('dataset,cell')
        .eq('user_id', uid).order('cell', { ascending: true }).range(from, from + 999);
      if (error || !data) return null;
      rows.push(...(data as any[]));
      if (data.length < 1000) break;
    }
    const keys = new Set(ds ? datasetTagVariants(ds).map(datasetKey) : []);
    const here = rows.filter(r => keys.has(datasetKey(r.dataset))).length;
    return { label: ds ? (ds.shortLabel || ds.label) : '', here, all: rows.length };
  } catch { return null; }
}

/** The cells CAVE's copy of a dataset's own records credits to a player,
 *  as root ids. Empty when the player has no CAVE id or it cannot be read.
 *  One read covers every dataset (player_summary.ts). */
async function mirroredCells(uid: string, tags: string[]): Promise<Set<string>> {
  const want = new Set(tags);
  const rows = await caveMirrorCells(uid);
  return new Set((rows ?? []).filter(r => want.has(r.dataset)).map(r => r.segment));
}

/** Splits and merges that went through: the same rows the board counts. From
 *  the player's hourly totals when they exist, else counted in edit_log. */
async function editCount(uid: string, tags: string[]): Promise<number> {
  const hours = await editHours(uid);
  if (hours) {
    const want = new Set(tags);
    return hours.reduce((s, h) => s + (want.has(h.dataset) ? h.merges + h.splits : 0), 0);
  }
  const { supabase } = await import('../supabase');
  const r: any = await supabase.from('edit_log').select('id', { count: 'exact', head: true })
    .eq('user_id', uid).in('dataset', tags).in('operation', ['split', 'merge']).not('success', 'is', false);
  return r.count ?? 0;
}

async function helpRequestCount(uid: string, tags: string[]): Promise<number> {
  const asked = await helpRequestDatasets(uid);
  if (asked) {
    const want = new Set(tags);
    return asked.filter(d => want.has(d)).length;
  }
  const { supabase } = await import('../supabase');
  const r: any = await supabase.from('help_requests').select('id', { count: 'exact', head: true })
    .eq('user_id', uid).in('dataset', tags);
  return r.count ?? 0;
}

export async function loadContribution(ds: DatasetEntry, uid: string): Promise<DatasetContribution> {
  const tags = datasetTagVariants(ds);
  const [edits, helpRequests, logged, mirror] = await Promise.all([
    editCount(uid, tags),
    helpRequestCount(uid, tags),
    loggedCells(uid, tags),
    // Only where most work happens in other tools. On the game's own datasets
    // the game's log is the whole record.
    showsAllStats(ds) ? Promise.resolve(new Set<string>()) : mirroredCells(uid, tags),
  ]);
  // A cell completed in the game is already in cells completed: not twice.
  for (const c of logged ?? []) for (const r of c.roots) mirror.delete(r);
  return { edits, completions: logged?.length ?? 0, helpRequests, outside: mirror.size };
}
